import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// @mui material components
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getModels, deleteModel } from "../../../../api/models";
import { setAllSuppliers } from "../../../../redux/reducers/suppliersReducer";
import { getSuppliers } from "../../../../api/suppliers";

import DeleteModal from "../../../../customComponents/devicesComponents/DeleteModal";
import CreateEditModel from "../../../../customComponents/adminComponents/CreateEditModel";

import {
  setModelSupplier,
  setModelValueBigFilter,
  setModelSelectBigFilter,
} from "../../../../redux/reducers/filtersReducer";

export default function Models() {
  useAuthCheck();
  const { user } = useAuth();
  const [models, setModels] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const modelSupplier = useSelector((state) => state.filters.modelSupplier);
  const modelValueBigFilter = useSelector((state) => state.filters.modelValueBigFilter);
  const modelSelectBigFilter = useSelector((state) => state.filters.modelSelectBigFilter);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const fetchSuppliers = async () => {
    const response = await getSuppliers();
    const formatedSuppliers = await response.data.map((supplier) => ({
      label: supplier.supplier_name,
      id: supplier.supplier_id,
    }));
    dispatch(setAllSuppliers(formatedSuppliers));
  };

  const allSuppliers = useSelector((state) => state.suppliers.allSuppliers);

  const bigFilterInfo = {
    Nom: "tblmodel.model_name",
  };

  const [query, setQuery] = useState({});

  const updateQueryBasedOnFilters = () => {
    let updatedQuery = {};
    if (modelSupplier) updatedQuery["tblmodel.supplier_id"] = modelSupplier?.id || null;
    if (modelSelectBigFilter) {
      updatedQuery[bigFilterInfo[modelSelectBigFilter]] = encodeURIComponent(modelValueBigFilter);
    }
    return updatedQuery;
  };

  const fetchModelsWithFilters = async (additionalQuery = {}) => {
    if (user) {
      const filtersQuery = updateQueryBasedOnFilters();
      const response = await getModels({ ...filtersQuery, ...query, ...additionalQuery });
      setModels(response.data);
    }
  };

  function updateQuery(key, value) {
    if (value === null)
      setQuery((query) => {
        const copy = { ...query };
        delete copy[key];
        return copy;
      });
    else setQuery((query) => ({ ...query, [key]: value }));
  }

  const dispatch = useDispatch();

  const [controller] = useMaterialUIController();
  const { darkMode, cardBoard } = controller;

  useEffect(() => {
    if (user && user.role !== "Admin" && user.role !== "Pilote" && user.role !== "Contremaître") {
      navigate("/accueil");
    } else {
      fetchModelsWithFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelSupplier, modelValueBigFilter, modelSelectBigFilter, dispatch, user]);

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function deleteModelAPI(model) {
    await deleteModel(model);
  }

  function paginate(array, page_size, page_number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  async function submit(model) {
    await deleteModelAPI(model);
    await fetchModelsWithFilters();
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={1.5}>
        <Grid container justifyContent="center" direction="row" spacing={2}>
          <Grid
            item
            lg={2.8}
            md={4}
            sm={5}
            xs={10}
            sx={{
              border: "1px solid lightGrey",
              borderRadius: "8px",
              marginTop: 2,
              marginLeft: 2,
              padding: "1rem !important",
            }}
          >
            <MDTypography variant="subtitle1" align="center">
              Filtre de précision
            </MDTypography>
            <Tooltip
              title={modelSelectBigFilter ? "" : "Veuillez sélectionner un élément à cibler"}
              placement="top"
            >
              <div>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={modelValueBigFilter}
                  onChange={(event) => {
                    dispatch(setModelValueBigFilter(event.target.value));
                    updateQuery(bigFilterInfo[modelSelectBigFilter], event.target.value);
                    setPage(1);
                  }}
                  inputProps={{
                    style: {
                      height: 29.13,
                    },
                    maxLength: 255,
                  }}
                  type="text"
                  label="Valeur à rechercher"
                  disabled={!modelSelectBigFilter}
                  sx={{ marginBottom: 2, marginTop: 1 }}
                />
              </div>
            </Tooltip>
            <Autocomplete
              options={Object.keys(bigFilterInfo)}
              value={modelSelectBigFilter}
              onChange={(event, newBigFilter) => {
                if (newBigFilter) {
                  // If a new filter is selected, update the query and keep the current input value
                  dispatch(setModelSelectBigFilter(newBigFilter));
                } else {
                  // If the filter is cleared, remove the corresponding query part and clear the input
                  setQuery((currentQuery) => {
                    const newQuery = { ...currentQuery };
                    Object.values(bigFilterInfo).forEach((filterKey) => {
                      if (filterKey in newQuery) {
                        delete newQuery[filterKey];
                      }
                    });
                    return newQuery;
                  });
                  dispatch(setModelSelectBigFilter(null));
                  dispatch(setModelValueBigFilter(""));
                }
                setPage(1);
              }}
              renderInput={(params) => (
                <MDInput pb={0} {...params} label="Élément à cibler" variant="outlined" />
              )}
            />
          </Grid>

          <Grid
            item
            container
            direction="column"
            justifyContent="center"
            spacing={2}
            lg={2.4}
            md={4}
            sm={5}
            xs={10}
            /* lg={4.4}
            md={4}
            sm={5}
            xs={11} */
            sx={{
              padding: "1rem !important",
              border: "1px solid lightGrey",
              borderRadius: "8px",
              textAlign: "center",
              marginTop: 2,
              marginLeft: 2,
            }}
          >
            <MDTypography mb={1} variant="subtitle1" align="center">
              Filtres normaux
            </MDTypography>
            <Grid direction="row" spacing={2} container justifyContent="center">
              <Grid item xs={12}>
                <Autocomplete
                  options={allSuppliers}
                  value={modelSupplier}
                  onChange={(event, newSupplier) => {
                    dispatch(setModelSupplier(newSupplier));
                    updateQuery("tblmodel.supplier_id", newSupplier?.id || null);
                    setPage(1);
                  }}
                  renderInput={(params) => (
                    <TextField pb={0} {...params} label="Fournisseur" variant="outlined" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            direction="row"
            item
            container
            justifyContent="center"
            lg={12}
            md={12}
            sm={12}
            xs={12}
          >
            <Grid item sx={{ padding: 1, marginTop: 3 }} lg={3} md={6} sm={6} xs={12}>
              <CreateEditModel
                models={models}
                fetchModelsWithFilters={fetchModelsWithFilters}
                edit={false}
              />
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {models && models.length > 0 ? (
            !cardBoard ? (
              paginate(models, 12, page).map((model) => (
                <Grid item mb={1.5} lg={3} md={6} xs={12}>
                  <Card sx={{ padding: "0 1rem" }}>
                    <MDBox
                      item
                      mx={0.5}
                      mt={-3}
                      py={3}
                      px={2}
                      sx={{
                        background: !darkMode
                          ? "linear-gradient(195deg, #364c70, #273956)"
                          : "white",
                        boxShadow: `0 1px 6px 1px ${!darkMode ? "#344767" : "white"}`,
                      }}
                      variant="gradient"
                      borderRadius="lg"
                    >
                      <MDTypography
                        variant="h5"
                        textAlign="center"
                        color={!darkMode ? "white" : "#344767"}
                      >
                        {model.model_name ? model.model_name : "Nom inconnu"}
                      </MDTypography>
                      <MDTypography
                        mt={-0.7}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        De{" "}
                        <b>
                          {model.supplier_name !== null
                            ? model.supplier_name
                            : "Fournisseur inconnu"}
                        </b>
                      </MDTypography>
                    </MDBox>
                    <MDBox textAlign="center" pb={3} pt={3}>
                      <Grid
                        container
                        rowSpacing={1.5}
                        columnSpacing={{ xs: 2 }}
                        justifyContent="center"
                      >
                        <Grid item>
                          <CreateEditModel
                            models={models}
                            fetchModelsWithFilters={fetchModelsWithFilters}
                            model={model}
                            edit
                          />
                        </Grid>
                        <Grid item>
                          <DeleteModal
                            message={`le modèle ${model.model_name}`}
                            onOk={() => {
                              submit(model.model_id);
                            }}
                            type="model"
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              ))
            ) : (
              paginate(models, 12, page).map((model) => (
                <Grid
                  item
                  container
                  xs={12}
                  sx={{ paddingTop: "0px !important", marginBottom: "0px !important" }}
                >
                  <Grid item mt={0.5} mb={0.5} xs={12}>
                    <Divider
                      sx={{
                        margin: "0.5rem 0rem",
                        background: !darkMode ? "#000" : "#FFF",
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
                  >
                    <Grid mt={0.75} item lg={2.5} xs={12}>
                      <MDTypography variant="h5" textAlign="center">
                        {model.model_name ? model.model_name : "Nom inconnu"}
                      </MDTypography>
                      <MDTypography
                        mt={-0.7}
                        sx={{ opacity: "0.7" }}
                        fontSize="0.75rem"
                        textTransform="uppercase"
                        textAlign="center"
                      >
                        De{" "}
                        <b>
                          {model.supplier_name !== null
                            ? model.supplier_name
                            : "Fournisseur inconnu"}
                        </b>
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <CreateEditModel
                        models={models}
                        fetchModelsWithFilters={fetchModelsWithFilters}
                        model={model}
                        edit
                      />
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <DeleteModal
                        message={`le modèle ${model.model_name}`}
                        onOk={() => {
                          submit(model.model_id);
                        }}
                        type="model"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))
            )
          ) : (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Aucun modèle à afficher
              </MDTypography>
            </Grid>
          )}
          {models &&
            (models.length <= 12 ? (
              <></>
            ) : (
              <Grid item container xs={12} mt={3} justifyContent="center">
                <Stack spacing={2}>
                  <Pagination
                    page={page}
                    onChange={handlePageChange}
                    variant="text"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      button: {
                        color: !darkMode ? "#1A2035" : "#FFF",
                      },
                      "button.Mui-selected": { color: !darkMode ? "#FFF" : "#1A2035" },
                    }}
                    count={Math.ceil(models.length / 12)}
                    color={darkMode ? "white" : "dark"}
                  ></Pagination>
                </Stack>
              </Grid>
            ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
